<section class="px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <p class="mb-6">
      <a routerLink="/competitions" class="text-sm text-dusk hover:text-parchment hover:underline"
        >&larr; Back to my competitions</a
      >
    </p>

    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else if (competition(); as c) {
      <div class="mb-4 flex items-start justify-between gap-4">
        <div>
          <span class="mb-2 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
            >Competition</span
          >
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="font-display text-3xl font-semibold text-parchment">{{ c.name }}</h1>
            @if (c.ownerIsSiteAdmin && showAdminControls()) {
              <span
                class="shrink-0 rounded-full border border-dusk px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-dusk"
                >Admin-owned</span
              >
            }
            <button
              type="button"
              (click)="infoExpanded.set(!infoExpanded())"
              class="text-xs text-dusk hover:text-parchment hover:underline"
            >
              Info {{ infoExpanded() ? '⌃' : '⌄' }}
            </button>
          </div>
        </div>
        <span
          class="shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide"
          [class.border-gold]="c.status === 'ACTIVE'"
          [class.text-gold]="c.status === 'ACTIVE'"
          [class.border-wax-bright]="c.status === 'ON_HOLD'"
          [class.text-wax-bright]="c.status === 'ON_HOLD'"
          >{{ c.status === 'ACTIVE' ? 'Active' : 'On hold' }}</span
        >
      </div>

      @if (infoExpanded()) {
        <app-competition-message
          [competitionId]="c.id"
          [message]="c.message"
          [isOwner]="isOwner()"
          (changed)="onEditModalChanged()"
        />

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="rounded-md border border-hairline bg-panel p-6">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Dates</h2>
            <dl class="mt-3 flex flex-col gap-2 text-sm text-parchment">
              <div class="flex justify-between">
                <dt class="text-dusk">Start</dt>
                <dd>{{ dateOnly(c.startDate) }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-dusk">End</dt>
                <dd>{{ dateOnly(c.endDate) }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-dusk">Submission cutoff</dt>
                <dd>{{ dateOnly(c.submissionEndDate) }}</dd>
              </div>
            </dl>
          </div>

          <div class="rounded-md border border-hairline bg-panel p-6">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Roster rules</h2>
            <dl class="mt-3 flex flex-col gap-2 text-sm text-parchment">
              <div class="flex justify-between">
                <dt class="text-dusk">Regular picks</dt>
                <dd>{{ c.maxEntries }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-dusk">Alternates</dt>
                <dd>{{ c.maxAlternates }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-dusk">Max age</dt>
                <dd>{{ c.maxAge }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Achievements</h2>
          <div class="mt-3">
            <app-achievements-list [achievements]="achievements()" />
          </div>
        </div>
      }

      @if (isContestant()) {
        <app-competition-my-picks
          [competitionId]="c.id"
          [picks]="myPicks()"
          [leaderboardEntry]="myLeaderboardEntry()"
          [pastCutoff]="pastCutoff()"
        />
      }

      <!-- Management panels sit side by side from the lg breakpoint up; each is
           a direct child of this grid, so the panels' own mt-4 is zeroed. -->
      <div class="mt-4 grid items-start gap-4 lg:grid-cols-2 [&>*]:!mt-0">
      @if (showAdminControls()) {
        <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Site admin</h2>
          <p class="mt-2 text-sm text-dusk">
            You're not the owner of this competition, but as a site admin you can edit or
            delete it.
          </p>
          <button
            type="button"
            (click)="editModalOpen.set(true)"
            class="mt-3 inline-flex items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-xs font-semibold text-parchment transition hover:border-parchment"
          >
            Edit competition
          </button>
        </div>

        <ng-container
          *ngTemplateOutlet="
            contestantsPanel;
            context: { canManage: true, canAddContestant: true }
          "
        ></ng-container>

        <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Achievements</h2>
          @if (achievements().length === 0) {
            <p class="mt-3 text-sm text-dusk">No achievements configured yet.</p>
          } @else {
            <ul class="mt-3 flex flex-col gap-2">
              @for (a of achievements(); track a.id) {
                <li class="flex items-center justify-between rounded border border-hairline px-4 py-2 text-sm">
                  <span class="text-parchment">{{ a.achievementDefinition?.name ?? a.name }}</span>
                  @if (a.isFixed) {
                    <span class="text-xs uppercase tracking-wide text-dusk">Fixed</span>
                  } @else if (confirmingRemoveAchievementId() === a.id) {
                    <span class="flex items-center gap-2 text-xs">
                      <span class="text-parchment">Remove?</span>
                      <button
                        type="button"
                        (click)="confirmRemoveAchievement(a)"
                        [disabled]="removingAchievement()"
                        class="font-semibold text-wax-bright hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {{ removingAchievement() ? 'Removing…' : 'Yes' }}
                      </button>
                      <button
                        type="button"
                        (click)="confirmingRemoveAchievementId.set(null)"
                        class="text-dusk hover:text-parchment"
                      >
                        Cancel
                      </button>
                    </span>
                  } @else {
                    <button
                      type="button"
                      (click)="confirmingRemoveAchievementId.set(a.id)"
                      class="text-xs text-wax-bright hover:underline"
                    >
                      Remove
                    </button>
                  }
                </li>
              }
            </ul>
          }
          @if (removeAchievementError()) {
            <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ removeAchievementError() }}
            </p>
          }
        </div>

        <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">
            Awards ({{ adminAwards().length }})
          </h2>
          <p class="mt-2 text-xs text-dusk">
            Editing or removing an award for an automatically-scored achievement (e.g. Last
            Gasp, First to Die) may be overwritten the next time a death is recorded in this
            competition.
          </p>
          @if (adminAwards().length === 0) {
            <p class="mt-3 text-sm text-dusk">No awards yet.</p>
          } @else {
            <ul class="mt-3 flex flex-col gap-2">
              @for (award of adminAwards(); track award.awardId) {
                <li class="rounded border border-hairline px-4 py-2 text-sm">
                  <div class="flex items-center justify-between">
                    <span class="text-parchment"
                      >{{ award.contestantDisplayName }} &mdash; {{ award.name }}</span
                    >
                    <span class="text-dusk">{{ award.points ?? award.prizeDescription ?? '—' }}</span>
                  </div>
                  <div class="mt-2 flex items-center gap-3 text-xs">
                    @if (editingAwardId() === award.awardId) {
                      <input
                        type="number"
                        [formControl]="editAwardPointsControl"
                        class="w-24 rounded border border-hairline bg-ink px-2 py-1 text-parchment focus:border-gold focus:outline-none"
                      />
                      <button
                        type="button"
                        (click)="saveAwardEdit(award)"
                        [disabled]="savingAward()"
                        class="font-semibold text-gold hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {{ savingAward() ? 'Saving…' : 'Save' }}
                      </button>
                      <button
                        type="button"
                        (click)="editingAwardId.set(null)"
                        class="text-dusk hover:text-parchment"
                      >
                        Cancel
                      </button>
                    } @else if (confirmingRemoveAwardId() === award.awardId) {
                      <span class="text-parchment">Remove?</span>
                      <button
                        type="button"
                        (click)="confirmRemoveAward(award)"
                        [disabled]="removingAward()"
                        class="font-semibold text-wax-bright hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {{ removingAward() ? 'Removing…' : 'Yes' }}
                      </button>
                      <button
                        type="button"
                        (click)="confirmingRemoveAwardId.set(null)"
                        class="text-dusk hover:text-parchment"
                      >
                        Cancel
                      </button>
                    } @else {
                      <button
                        type="button"
                        (click)="startEditAward(award)"
                        class="text-parchment hover:text-gold hover:underline"
                      >
                        Edit points
                      </button>
                      <button
                        type="button"
                        (click)="confirmingRemoveAwardId.set(award.awardId)"
                        class="text-wax-bright hover:underline"
                      >
                        Remove
                      </button>
                    }
                  </div>
                </li>
              }
            </ul>
          }
          @if (awardError()) {
            <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ awardError() }}
            </p>
          }
        </div>
      }

      @if (isOwner()) {
        @if (c.status === 'ON_HOLD') {
          <p class="rounded border border-wax-bright/40 bg-wax/10 px-4 py-3 text-sm text-parchment lg:col-span-2">
            This competition is on hold — only the owner has access until the plan or contestant
            count is resolved.
          </p>
        }

        <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Manage</h2>
          <p class="mt-2 text-sm text-dusk">
            Edit the dates, roster rules, or delete this competition.
          </p>
          <div class="mt-3 flex flex-wrap gap-3">
            <button
              type="button"
              (click)="editModalOpen.set(true)"
              class="inline-flex items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-xs font-semibold text-parchment transition hover:border-parchment"
            >
              Edit competition
            </button>
            @if (canManageAchievements()) {
              <a
                [routerLink]="['/competitions', c.id, 'achievements']"
                class="inline-flex items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-xs font-semibold text-parchment transition hover:border-parchment"
              >
                Manage achievements
              </a>
            }
          </div>
        </div>

        <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Invite</h2>
          <p class="mt-3 font-mono text-sm text-parchment">
            Code: <span class="text-gold">{{ c.shortCode }}</span>
          </p>
          <div class="mt-3 flex flex-wrap items-center gap-3">
            <code class="flex-1 truncate rounded border border-hairline bg-ink px-3 py-2 text-xs text-dusk">{{
              joinLink()
            }}</code>
            <button
              type="button"
              (click)="copyJoinLink()"
              class="inline-flex shrink-0 items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-xs font-semibold text-parchment transition hover:border-parchment"
            >
              {{ copied() ? 'Copied!' : copyFailed() ? "Couldn't copy" : 'Copy link' }}
            </button>
          </div>
        </div>

        <ng-container
          *ngTemplateOutlet="
            contestantsPanel;
            context: { canManage: false, canAddContestant: canAddContestant() }
          "
        ></ng-container>
      }
      </div>
    }
  </div>
</section>

<ng-template #contestantsPanel let-canManage="canManage" let-canAddContestant="canAddContestant">
  <div class="mt-4 rounded-md border border-hairline bg-panel p-6">
    <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">
      Contestants ({{ contestants().length }})
    </h2>
    @if (contestants().length === 0) {
      <p class="mt-3 text-sm text-dusk">No one has joined yet.</p>
    } @else {
      <ul class="mt-3 flex flex-col gap-2">
        @for (contestant of contestants(); track contestant.id) {
          <li class="flex items-center justify-between rounded border border-hairline px-4 py-2 text-sm">
            <button
              type="button"
              (click)="openInfo(contestant)"
              class="text-parchment hover:text-gold hover:underline"
            >
              {{ contestant.displayName }}
            </button>
            <div class="flex items-center gap-3">
              <button
                type="button"
                (click)="openEntries(contestant)"
                class="text-dusk hover:text-parchment hover:underline"
              >
                {{ pickSummary(contestant) }}
              </button>
              @if (canManage) {
                @if (confirmingKickUserId() === contestant.id) {
                  <span class="flex items-center gap-2 text-xs">
                    <span class="text-parchment">Remove?</span>
                    <button
                      type="button"
                      (click)="confirmKick(contestant)"
                      [disabled]="kicking()"
                      class="font-semibold text-wax-bright hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {{ kicking() ? 'Removing…' : 'Yes' }}
                    </button>
                    <button
                      type="button"
                      (click)="confirmingKickUserId.set(null)"
                      class="text-dusk hover:text-parchment"
                    >
                      Cancel
                    </button>
                  </span>
                } @else {
                  <button
                    type="button"
                    (click)="confirmingKickUserId.set(contestant.id)"
                    class="text-xs text-wax-bright hover:underline"
                  >
                    Remove
                  </button>
                }
              }
            </div>
          </li>
        }
      </ul>
    }

    @if (kickError()) {
      <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
        {{ kickError() }}
      </p>
    }

    @if (canAddContestant) {
      <form
        [formGroup]="addContestantForm"
        (ngSubmit)="addContestant()"
        class="mt-4 flex flex-col gap-2 border-t border-hairline pt-4 sm:flex-row"
      >
        <input
          formControlName="email"
          type="email"
          placeholder="Add contestant by email…"
          class="flex-1 rounded border border-hairline bg-ink px-3 py-2 text-sm text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
        />
        <input
          formControlName="displayName"
          type="text"
          placeholder="Display name (only if new)…"
          class="flex-1 rounded border border-hairline bg-ink px-3 py-2 text-sm text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
        />
        <button
          type="submit"
          [disabled]="addingContestant()"
          class="inline-flex shrink-0 items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-xs font-semibold text-parchment transition hover:border-parchment disabled:cursor-not-allowed disabled:opacity-50"
        >
          {{ addingContestant() ? 'Adding…' : 'Add' }}
        </button>
      </form>
      <p class="mt-1 text-[0.7rem] text-dusk">
        Display name is only used when the email doesn't match an existing account — it
        creates a new, unverified contestant under that name (site admin only).
      </p>
      @if (addContestantError()) {
        <p class="mt-2 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
          {{ addContestantError() }}
        </p>
      }
    }
  </div>
</ng-template>

@if (entriesModalContestant(); as contestant) {
  <app-entries-modal
    [competitionId]="competition()!.id"
    [userId]="contestant.userId"
    [displayName]="contestant.displayName"
    [maxEntries]="competition()!.maxEntries"
    [maxAlternates]="competition()!.maxAlternates"
    (closed)="closeEntriesModal()"
  />
}

@if (infoModalContestant(); as contestant) {
  <app-contestant-modal
    [competitionId]="competition()!.id"
    [userId]="contestant.userId"
    [displayName]="contestant.displayName"
    [email]="contestant.email"
    (closed)="infoModalContestant.set(null)"
  />
}

@if (editModalOpen() && competition(); as c) {
  <app-competition-edit-modal
    [competition]="c"
    [redirectAfterDelete]="isOwner() ? '/competitions' : '/admin/competitions'"
    (closed)="editModalOpen.set(false)"
    (changed)="onEditModalChanged()"
  />
}
